import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import SimpleDropdown from "../../components/simpleDropdown";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import { updateCity, getCityDetails } from "./citySlice";
import { cityEditSchema } from "../../schema/validationSchemas";
import { getCountries, getStates } from "../Settings/settingSlice";

export default function Edit() {
  const { id: cityId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const [city, setCity] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  const fetchCountries = useCallback(async () => {
    try {
      await dispatch(getCountries({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((country) => ({
              value: country.id,
              label: country.name,
            }));
            setCountriesOptions(formattedData);
          }
        } else {
          setCountriesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch]);

  const fetchStates = useCallback(async () => {
    try {
      const payload = {
        country_id: selectedCountryId,
      };
      await dispatch(getStates({ payload })).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((state) => ({
              value: state.id,
              label: state.name,
            }));
            setStatesOptions(formattedData);
          }
        } else {
          setStatesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch, selectedCountryId]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (selectedCountryId) {
      fetchStates(selectedCountryId);
    }
  }, [selectedCountryId, fetchStates]);

  const fetchCity = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getCityDetails(cityId));
      const cityData = result.payload.data;
        setSelectedCountryId(cityData.country_id);
        setCity(cityData);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching city:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, cityId, setLoading]);

  useEffect(() => {
    setCity(null);
    if (cityId) {
      fetchCity();
    }
  }, [cityId, fetchCity]);

  const handleCancel = () => {
    setCity(null);
    navigate("/cities");
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: cityId };

    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("_method", "PUT");
    formData.append("name", updatedValues.name);
    formData.append("country_id", updatedValues.country_id);
    formData.append("state_id", updatedValues.state_id);

    await dispatch(updateCity(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("city_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setCity(null);
          navigate("/cities");
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to update city!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update city!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  if (!city)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading City Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      {city && (
        <Formik
          initialValues={{
            name: city.name || "",
            country_id: city.country_id || "",
            state_id: city.state_id || "",
          }}
          validationSchema={cityEditSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <Form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="bg-white shadow-lg rounded-lg p-4"
            >
              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <Row>
                  <Col md={6}>
                    <CommonFormLabel htmlFor="name" title="Name" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter Name"
                      onChange={handleChange}
                      value={values.name}
                      className={`shadow-none ${
                        errors?.name && touched.name ? "error" : "correct"
                      }`}
                    />
                    {touched.name && errors?.name && (
                      <ValidationFeedback title={errors?.name} />
                    )}
                  </Col>
                  <Col md={6}>
                    <SimpleDropdown
                      label="Country"
                      name="country_id"
                      value={values.country_id}
                      onChange={(selectedOption) => {
                        setFieldValue("country_id", selectedOption.value);
                        setFieldValue("state_id", "");
                        setSelectedCountryId(selectedOption.value);
                      }}
                      options={countriesOptions}
                      placeholder="Select a Country"
                      error={errors?.country_id}
                      touched={touched.country_id}
                    />
                  </Col>
                  <Col md={6}>
                    <SimpleDropdown
                      label="State"
                      name="state_id"
                      value={values.state_id}
                      onChange={(selectedOption) => {
                        setFieldValue("state_id", selectedOption.value);
                      }}
                      options={statesOptions}
                      placeholder="Select a State"
                      error={errors?.state_id}
                      touched={touched.state_id}
                    />
                  </Col>
                </Row>
              </Card>

              <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                <CommonButton
                  title={isSubmitting ? "Updating..." : "Update"}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary primary-shadow"
                />
                <CommonButton
                  title="Cancel"
                  onClick={handleCancel}
                  className="btn-muted"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
